import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  type Connection,
  type Edge,
  type EdgeChange,
  type NodeChange,
  type XYPosition,
} from "@xyflow/react";
import { create } from "zustand";
import { evaluate } from "~/lib/engine";
import {
  createNodeId,
  deserializeGraph,
  edgeId,
  serializeGraph,
  type SpiroNode,
} from "~/lib/graph";
import type { OutputPayload } from "~/lib/types";
import { getDefinition } from "~/nodes/registry";

/** Node types whose output changes over time, so the graph must be re-run continuously. */
export const LIVE_TYPES = new Set(["animate", "audio"]);

/** Node changes that can alter evaluation results. */
const STRUCTURAL_NODE_CHANGES = new Set<NodeChange["type"]>([
  "add",
  "remove",
  "replace",
]);

interface GraphStore {
  nodes: SpiroNode[];
  edges: Edge[];

  /** Draw calls for the viewer, keyed by Output node id. */
  outputs: Record<string, OutputPayload>;
  previews: Record<string, Record<string, number>>;
  errors: Record<string, Record<string, string>>;

  /** Bumped whenever the whole graph is replaced, so the view can re-fit. */
  generation: number;

  onNodesChange: (changes: NodeChange<SpiroNode>[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;

  addNode: (type: string, position: XYPosition) => void;
  duplicateNode: (id: string) => void;
  deleteNode: (id: string) => void;
  setNodeData: (id: string, key: string, value: unknown) => void;

  loadGraph: (raw: unknown) => void;
  toJSON: () => ReturnType<typeof serializeGraph>;
  clear: () => void;

  /** Re-evaluates the graph. `time` is seconds since the editor mounted. */
  run: (time?: number) => void;
  /** True when at least one node needs the graph re-run every frame. */
  isLive: () => boolean;
}

export const useGraphStore = create<GraphStore>((set, get) => {
  /** Evaluates and stores results, reusing object identities where nothing changed. */
  const run = (time = 0): void => {
    const { nodes, edges } = get();
    const result = evaluate(nodes, edges, time);

    set({
      outputs: result.outputs,
      previews: result.previews,
      errors: result.errors,
    });
  };

  return {
    nodes: [],
    edges: [],
    outputs: {},
    previews: {},
    errors: {},
    generation: 0,

    onNodesChange(changes) {
      set((state) => ({ nodes: applyNodeChanges(changes, state.nodes) }));

      // Dragging, selecting and measuring can't change results — and xyflow emits
      // a `dimensions` change for every node on mount, so re-running on those
      // would evaluate the graph once per node on every load.
      if (changes.some((change) => STRUCTURAL_NODE_CHANGES.has(change.type))) {
        run(currentTime());
      }
    },

    onEdgesChange(changes) {
      set((state) => ({ edges: applyEdgeChanges(changes, state.edges) }));
      if (changes.some((change) => change.type !== "select"))
        run(currentTime());
    },

    onConnect(connection) {
      if (!connection.sourceHandle || !connection.targetHandle) return;

      set((state) => ({
        edges: addEdge(
          {
            ...connection,
            id: edgeId(
              connection.source,
              connection.sourceHandle!,
              connection.target,
              connection.targetHandle!,
            ),
          },
          // An input takes one value; replace whatever was plugged in before.
          state.edges.filter(
            (edge) =>
              !(
                edge.target === connection.target &&
                edge.targetHandle === connection.targetHandle
              ),
          ),
        ),
      }));

      run(currentTime());
    },

    addNode(type, position) {
      const definition = getDefinition(type);
      if (!definition) return;

      set((state) => ({
        nodes: [
          ...state.nodes,
          {
            id: createNodeId(),
            type,
            position,
            data: { ...definition.defaults },
          },
        ],
      }));

      run(currentTime());
    },

    duplicateNode(id) {
      const source = get().nodes.find((node) => node.id === id);
      if (!source) return;

      set((state) => ({
        nodes: [
          ...state.nodes,
          {
            id: createNodeId(),
            type: source.type,
            position: { x: source.position.x + 40, y: source.position.y + 40 },
            data: { ...source.data },
          },
        ],
      }));

      run(currentTime());
    },

    deleteNode(id) {
      set((state) => ({
        nodes: state.nodes.filter((node) => node.id !== id),
        edges: state.edges.filter(
          (edge) => edge.source !== id && edge.target !== id,
        ),
      }));

      run(currentTime());
    },

    setNodeData(id, key, value) {
      set((state) => ({
        nodes: state.nodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, [key]: value } }
            : node,
        ),
      }));

      run(currentTime());
    },

    loadGraph(raw) {
      const { nodes, edges } = deserializeGraph(raw);

      set((state) => ({
        nodes,
        edges,
        outputs: {},
        previews: {},
        errors: {},
        generation: state.generation + 1,
      }));

      run(currentTime());
    },

    toJSON() {
      const { nodes, edges } = get();
      return serializeGraph(nodes, edges);
    },

    clear() {
      set((state) => ({
        nodes: [],
        edges: [],
        outputs: {},
        previews: {},
        errors: {},
        generation: state.generation + 1,
      }));
    },

    run,

    isLive() {
      return get().nodes.some((node) => LIVE_TYPES.has(node.type));
    },
  };
});

const start = performance.now();

/** Seconds since module load — the clock every time-varying node reads. */
export function currentTime(): number {
  return (performance.now() - start) / 1000;
}
